import type { CustomFontSize } from '../components/DialogHeader/DialogHeader';
import type { ButtonSkin, Size } from '@wix/design-system';
import nautilusGlyph from '../assets/images/nautilus-glyph.svg?url';
// BRIK wordmark, re-exported from Figma 105:10831 (85x25, no BETA pill).
import brikLogo from '../assets/images/brik-logo.svg?url';
// "Symphony by wix" brand lockup (monochrome charcoal), from the MKT Figma
import symphonyLogo from '../assets/images/symphony-logo.svg?url';
// Fade lockup (served on the fade userType): Afterimage symbol + outlined Inter
// wordmark, from imaxx design/brand.
import fadeLogo from '../assets/images/fade-logo.svg?url';
import wheneverLogo from '../assets/images/whenever-logo.svg?url';
// Explainers lockup: four-square mark + outlined Archivo ExtraBold wordmark,
// the same logo the Explainers app shows in its sidebar.
import explainersLogo from '../assets/images/explainers-logo.svg?url';

type Skin = 'light' | 'dark' | 'standard'; // 'light' should be used on dark backgrounds
export type LogoPosition = 'left' | 'center';

type ButtonDesign = {
  skin: ButtonSkin;
  textKey?: string;
  color?: string;
  fullWidth?: boolean;
  height?: string;
  fontSize?: string;
  showArrowOnButton?: boolean;
  hideSuffixIcon?: boolean; // Hide the ChevronRight suffix icon on EmailStep button (default: false, shows icon for horizontal layouts)
  // Label color (enabled/hover/pressed/focus). The WDS button reads the SHARED
  // token --wds-color-text-standard-primary-light (used across ~130 components),
  // so this is applied INLINE-scoped to the button (see ButtonWithLoader) rather
  // than as a global cssVariable.
  textColor?: string;
  // Disabled-state text color. The disabled fill is set via the button-only
  // token --wds-color-fill-standard-primary-disabled (cssVariables), but the
  // disabled text token (--wds-color-text-standard-primary-light) is the same
  // shared token, so it's also applied INLINE-scoped to the button.
  disabledTextColor?: string;
  // In-button spinner colors (WDS Loader inside ButtonWithLoader). Applied
  // inline on the button so they don't affect the full-page loader, which uses
  // the same --wds-loader-* vars (see loaderColors.fullPage).
  loaderColors?: {
    fill?: string; // spinner track (--wds-loader-fill)
    fillLoading?: string; // spinner arc (--wds-loader-fill-loading)
  };
};

export type SocialButtonDesign = {
  height?: string;
  borderColor?: string;
  borderWidth?: string;
  borderRadius?: string;
  backgroundColor?: string;
  textColor?: string; // Label color (default WDS #000624)
  hoverBackgroundColor?: string;
  hoverBorderColor?: string;
  activeBackgroundColor?: string;
  activeBorderColor?: string; // Pressed-state border (WDS :active sets no border)
  disabledBackgroundColor?: string; // Disabled fill (default is a hardcoded tint)
  disabledTextColor?: string; // Disabled label color
  errorBorderColor?: string; // Border when the provider returned an error
  loaderFill?: string; // In-button spinner track (--wds-loader-fill)
  loaderFillLoading?: string; // In-button spinner arc (--wds-loader-fill-loading)
  focusBackgroundColor?: string;
  focusOutline?: string;
  focusOutlineOffset?: string;
  iconTextLayout?: 'centered' | 'space-between';
  fontSize?: string;
};

export type SwitchLinkDesign = {
  color?: string; // Regular link color
  hoverColor?: string; // Hover state color
  focusRingColor?: string; // Focus ring outline color
  underline?: 'none' | 'always' | 'onHover'; // Underline behavior
  fontWeight?: string; // Font weight (e.g., '400', '500', '600')
};

export type DividerDesign = {
  textColor?: string; // Color for "or" text (default: #20303c)
  lineColor?: string; // Color for divider lines (default: #e0e5eb)
};

export type LoaderColors = {
  fill?: string; // Background arc color (--wds-loader-fill)
  fillLoading?: string; // Spinning arc color (--wds-loader-fill-loading)
};

type FormSpacing = {
  inputButtonGap?: string; // Gap between input and button when no forgot email link (CSS value, e.g. '12px', '24px', default '24px')
  inputForgotEmailGap?: string; // Gap between input and forgot email link when present (CSS value, e.g. '8px', '12px', defaults to inputButtonGap)
  forgotEmailButtonGap?: string; // Gap between forgot email link and button (CSS value, e.g. '12px', '24px', default '24px')
  policiesDividerSpacing?: {
    top?: string; // Spacing above horizontal divider before policies (CSS value, e.g. '32px', '44px', default '32px')
    bottom?: string; // Spacing below horizontal divider before policies text (CSS value, e.g. '24px', '44px', default '24px'). Ignored when centerVertically is true.
    centerVertically?: boolean; // When true, vertically centers the policies text between divider and card bottom (default: false)
  };
  titleSectionGap?: string; // Gap between title and subtitle in DialogHeader (CSS value, default: '3px' for vertical, 6px for horizontal)
  forgotEmailAlignment?: 'left' | 'center'; // Alignment of Forgot Email link in EmailStep (default: 'left' for horizontal, 'center' for vertical)
  enterCodeButtonLayout?: 'stacked' | 'horizontal'; // Button layout in EnterCode 2FA dialogs: 'stacked' = vertical full-width buttons (Verify on top, Resend below), 'horizontal' = side-by-side (default)
  // Total visual gap between input and button in secondary dialogs (e.g., SSO enter email)
  // MINIMUM: 15px - values below this are clamped to 15px (due to WDS InputWithLabel internal padding)
  secondaryDialogInputButtonGap?: {
    desktop?: string; // Desktop total gap (CSS value, e.g. '45px', default: '27px', min: '15px')
    mobile?: string; // Mobile total gap (CSS value, e.g. '45px', default: '27px', min: '15px')
  };
};

type Title = {
  key: string;
  font?: string;
  size?: Size;
};

type Container = {
  primary: {
    padding: string;
  };
  secondary: {
    padding: string;
  };
  borderRadius: string;
  boxShadow: string;
  fullScreen?: boolean; // When true, removes card styling and fills screen (for mobile full-screen dialogs)
};

type AuthDialog = {
  title?: Title;
  subtitle?: Title;
  flows?: {
    id: string;
    title: Title;
    subtitle?: Title;
  }[];
  primaryButtonDesign?: ButtonDesign;
  secondaryButtonDesign?: ButtonDesign;
  emailLabelKey?: string;
  dividerTextKey?: string; // Translation key for "or" divider text between social buttons and email input (vertical layout)
};

type SignupAuthDialog = AuthDialog & {
  showEmailCodeSignup?: boolean;
  emailFormButtonTextKey?: string; // Translation key for button in the email form step (with password fields)
};

type LoginAuthDialog = AuthDialog & {
  alreadyHaveAccount?: {
    title?: Title;
    subtitle?: Title;
  };
};

export const SocialProviders = {
  GOOGLE: 'google',
  FACEBOOK: 'facebook',
  APPLE: 'apple',
};

export const PRESETS = {
  DAYFUL: 'dayful',
  EDITORX: 'editorx',
  HOPP: 'hopp',
  EDUCATION_STUDENT: 'EDUCATION_STUDENT',
  EDUCATION_TEACHER: 'EDUCATION_TEACHER',
  CODUX: 'codux', // New branding for WixComponentsStudio (WCS)
  LOGO_MAKER: 'logo_maker',
  BUSINESS_CARD_MAKER: 'bcm',
  WIXEL: 'wixel',
  NAUTILUS: 'nautilus',
  PICASSO: 'picasso',
  WIX_2025: 'wix-2025',
  SYMPHONY: 'symphony',
  FADE: 'fade',
  WHENEVER: 'whenever',
  EXPLAINERS: 'explainers',
  BRIK: 'brik',
};

export type LogoConfig = Partial<{
  // Default - wix logo
  src: string;
  position: LogoPosition; // Default - 'left'
  height: string; // Default - 24px
  mobile: {
    height: string; // Default - 20px
  };
  alt: string; // Default - 'Wix'
  url: string; // Default - www.wix.com
  dividerColor: string; // Color for vertical divider between main and secondary logo (SSO screens)
}>;

export type ViewPreset = Partial<{
  id: string; // Default - preset key
  nameKey: string; // Default - 'presets.names.wix' = Wix
  skin: Skin; // Default - standard
  shouldDisablePostLoginSuggestions: boolean; // Default - false
  container: {
    layout: 'vertical' | 'horizontal'; // Default - horizontal
    desktop: Container;
    mobile: Container;
  };
  background: string;
  // Optional CSS class (in App.scss) applied to the app root for the page
  // background, as an alternative to the inline `background` string. Used when
  // the background references bundled image assets (kept out of the JS graph).
  backgroundClassName: string;
  login: LoginAuthDialog;
  signup: SignupAuthDialog;
  landingPage: AuthDialog; // Main landing page (email step)
  logo: LogoConfig;
  partners: Record<string, Partial<Omit<ViewPreset, 'partners'>>>;
  policies: {
    policyKey?: string; // Default - 'policies_section.signup_content'
    termsOfUseUrl?: string; // Default - wix terms of use
    privacyPolicyUrl?: string; // Default - wix privacy policy
    linkColor?: string; // Color for policy links (default: inherit)
    // Let the Minimal policies (Terms/Privacy links) size to their content
    // instead of the default 400px min-width. In the narrow vertical wrapper the
    // 400px block overflows and pushes the links off-center; fitting to content
    // lets them center. Opt-in per preset (default: false → legacy 400px).
    minimalFitContent?: boolean;
  };
  buttonDesign?: ButtonDesign;
  // Header back button ("‹ Back"). Consumed in MainHeader.
  backButtonDesign?: {
    chevronIcon?: boolean; // Use a chevron (‹) instead of the default arrow (←)
    fontWeight?: string; // Label weight (e.g. '600' for semibold)
    fontSize?: string; // Desktop label size (e.g. '16px')
    mobileFontSize?: string; // Mobile label size (e.g. '14px')
    iconSize?: string; // Desktop arrow/chevron icon size (CSS value, e.g. '18px')
    mobileIconSize?: string; // Mobile arrow/chevron icon size (CSS value)
  };
  socialButtonDesign?: SocialButtonDesign;
  // Values here override socialButtonDesign when new Google SDK FT is enabled
  newGoogleSdkSocialButtonDesign?: SocialButtonDesign;
  // Styling for dialog switch links (Sign Up, Log In, Continue with SSO)
  switchLinkDesign?: SwitchLinkDesign;
  // Layout mode: when true, the vertical-layout login and email-step screens
  // render social buttons ABOVE the email form (matching the signup screen /
  // the Symphony Figma) instead of below. Default - false.
  socialFirst?: boolean;
  // Styling for "or" divider between social buttons and email input
  dividerDesign?: DividerDesign;
  // Styling for error toast notifications (SectionHelper handles background and text colors)
  errorToastDesign?: {
    iconColor?: string; // Default - '#E62214'
  };
  loaderColors?: {
    fullPage?: LoaderColors;
  };
  // When true, shows a card-style full-page loader (e.g. wix-2025).
  showCardLoader?: boolean;
  formSpacing?: FormSpacing;
  // Social providers separated with comma (for example - 'google,facebook')
  // Default - all ('google,facebook','apple')
  socialProviders: string;
  titleCustomFontSize: {
    authPage: {
      desktop: CustomFontSize;
      mobile: CustomFontSize;
    };
    secondaryFlow: {
      desktop: CustomFontSize;
      mobile: CustomFontSize;
    };
  };
  // Custom font size / line-height for the preset subtitle (overrides the WDS
  // "small" size). Applied inline on the subtitle text in AuthPage.
  subtitleFontSize: {
    fontSize: string;
    lineHeight?: string;
    mobileFontSize?: string; // Overrides fontSize below the 810px breakpoint
    letterSpacing?: string; // e.g. '-0.01em' for -1%
  };
  smallContextualSubtitle?: boolean;
  cssVariables: Record<string, string>;
  useBrandTheme?: boolean; // Default - true. When false, skips WDS Brand theme to allow full CSS variable theming
  secondaryTextColor?: string; // Override WDS Brand theme's hardcoded secondary text color (#595D70 → custom)
  // Default - 'header' (back button in the header, below the logo). 'aboveDialog'
  // renders it above the dialog card instead.
  backButtonPosition?: 'header' | 'aboveDialog';
  mobileBackButtonConfig?: {
    position: 'header' | 'hidden'; // Where to show back button on mobile
    marginTop?: string; // Vertical spacing from logo
    marginStart?: string; // Horizontal margin
    iconTextGap?: string; // Gap between arrow icon and text
  };
  dialogVerticalPosition?: 'top' | 'centered'; // Default - 'top'. When 'centered', dialog is vertically centered with minimum top offset
  dialogMinTopOffset?: string; // Minimum distance from top when dialogVerticalPosition is 'centered' (CSS value, e.g. '100px')
}>;

export const skinToColor = {};
export const DEFAULT_LOGO_HEIGHT = '26px';
const COMMON_NEW_GOOGLE_SDK_SOCIAL_BUTTON_DESIGN: SocialButtonDesign = {
  borderColor: '#DADCE0',
  hoverBackgroundColor: '#4285f414',
  hoverBorderColor: '#D2E3FC',
  focusBackgroundColor: '#4285f414',
  focusOutline: '2px solid #00639B',
  focusOutlineOffset: '1px',
};

// Explainers' outlined pill social buttons. Used for both socialButtonDesign and
// newGoogleSdkSocialButtonDesign, so the wix-2025 Google-SDK values it would otherwise
// inherit (40px, 4px radius, blue hover/focus) never replace the brand look.
// Figma 105:10787: white 46px fields/buttons, square, #E1E1E1 hairline.
// Measured: Google/Facebook/Apple each 320x46 at 12px vertical gaps. Shared by
// socialButtonDesign and newGoogleSdkSocialButtonDesign so the Google button
// does not diverge under the new-SDK experiment.
const BRIK_SOCIAL_BUTTON_DESIGN: SocialButtonDesign = {
  height: '46px',
  borderColor: '#E1E1E1',
  borderWidth: '1px',
  borderRadius: '0',
  iconTextLayout: 'centered',
  fontSize: '12px',
  textColor: '#000000',
  hoverBackgroundColor: '#F7F7F7',
  hoverBorderColor: '#B5B5B5',
  activeBackgroundColor: '#EAE9E9',
  activeBorderColor: '#B5B5B5',
  disabledBackgroundColor: '#EAE9E9',
  disabledTextColor: '#B5B5B5',
  errorBorderColor: '#DF3336',
  loaderFill: '#F3F3F3',
  loaderFillLoading: '#000000',
  focusOutline: '1.5px solid #000000',
  focusOutlineOffset: '2px',
};

const EXPLAINERS_SOCIAL_BUTTON_DESIGN: SocialButtonDesign = {
  height: '43px',
  borderColor: '#969696',
  borderWidth: '1px',
  borderRadius: '9999px',
  iconTextLayout: 'centered',
  fontSize: '14px',
  textColor: '#000000',
  hoverBackgroundColor: '#F3F3F3',
  hoverBorderColor: '#969696',
  activeBackgroundColor: '#EAE9E9',
  activeBorderColor: '#969696',
  disabledBackgroundColor: '#EAE9E9',
  disabledTextColor: '#B5B5B5',
  errorBorderColor: '#D13B3B',
  loaderFill: '#F3F3F3',
  loaderFillLoading: '#000000',
  focusOutline: '1.5px solid #000000',
  focusOutlineOffset: '1px',
};

export const ViewPresets: {
  [key: string]: ViewPreset;
} = {
  [PRESETS.DAYFUL]: {
    nameKey: 'presets.names.dayful',
    landingPage: {
      title: {
        key: 'emailStep.dayful.title',
      },
    },
    signup: {
      title: {
        key: 'signup.title',
      },
      subtitle: {
        key: 'dayful_subtitle',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/dayful-logo.png',
      height: '34px',
      url: 'https://www.dayful.com/',
    },
  },
  [PRESETS.HOPP]: {
    nameKey: 'presets.names.hopp',
    landingPage: {
      title: {
        key: 'emailStep.hopp.title',
      },
    },
    signup: {
      title: {
        key: 'signup.title',
      },
      subtitle: {
        key: 'hopp_subtitle',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/Hopp%20by%20wix%20logo%20(1).svg',
      height: '22px',
      url: 'https://www.hopp.co/',
    },
    partners: {
      discount: {
        logo: {
          src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/discount-hopp-logo.svg',
          height: '36px',
          mobile: { height: '30px' },
        },
      },
    },
  },
  [PRESETS.NAUTILUS]: {
    nameKey: 'presets.names.nautilus',
    landingPage: {
      title: {
        key: 'emailStep.nautilus.title',
      },
    },
    logo: {
      src: nautilusGlyph,
      height: '24px',
      alt: 'Nautilus',
      url: 'https://manage.wix.com/nautilus-app/',
    },
    signup: {
      title: {
        key: 'signup.title',
      },
    },
  },
  [PRESETS.EDUCATION_STUDENT]: {
    id: PRESETS.EDUCATION_STUDENT,
    nameKey: 'presets.names.education',
    landingPage: {
      title: {
        key: 'emailStep.education.student.title',
      },
    },
    login: {
      subtitle: {
        key: 'login.education.subtitle',
      },
    },
    signup: {
      title: {
        key: 'signup.education.student.title',
      },
    },
    logo: {
      src: 'https://images-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/642436ff-70f5-4ebc-8fea-47fab46f7f73/1693247680916',
      url: 'https://www.wix.com/education',
    },
    policies: {
      termsOfUseUrl:
        'https://www.wix.com/tomorrow/classroom/platform/terms-of-use',
      privacyPolicyUrl:
        'https://www.wix.com/tomorrow/classroom/platform/privacy-policy',
    },
    socialProviders: SocialProviders.GOOGLE,
  },
  [PRESETS.EDUCATION_TEACHER]: {
    id: PRESETS.EDUCATION_TEACHER,
    nameKey: 'presets.names.education',
    landingPage: {
      title: {
        key: 'emailStep.education.teacher.title',
      },
    },
    login: {
      subtitle: {
        key: 'login.education.subtitle',
      },
    },
    signup: {
      title: {
        key: 'signup.education.teacher.title',
      },
    },
    logo: {
      src: 'https://images-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/642436ff-70f5-4ebc-8fea-47fab46f7f73/1693247680916',
      url: 'https://www.wix.com/education',
    },
    policies: {
      termsOfUseUrl:
        'https://www.wix.com/tomorrow/classroom/platform/terms-of-use',
      privacyPolicyUrl:
        'https://www.wix.com/tomorrow/classroom/platform/privacy-policy',
    },
    socialProviders: SocialProviders.GOOGLE,
  },
  [PRESETS.CODUX]: {
    logo: {
      src: 'https://wixplosives.github.io/codux-assets-storage/login/codux-logo.svg',
      position: 'center',
      height: '100px',
      mobile: {
        height: '80px',
      },
      url: 'https://www.codux.com/',
    },
    signup: {
      title: {
        key: 'signup.title',
      },
    },
    policies: {
      termsOfUseUrl: 'https://www.wix.com/about/terms-of-use',
      privacyPolicyUrl: 'https://www.wix.com/about/privacy',
    },
  },
  [PRESETS.EDITORX]: {
    nameKey: 'presets.names.editorx',
    landingPage: {
      title: {
        key: 'emailStep.editorx.title',
      },
    },
    signup: {
      title: {
        key: 'signup.title',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/EditorX_logo_36x168_black.svg',
      url: 'https://www.editorx.com/',
    },
  },
  [PRESETS.LOGO_MAKER]: {
    nameKey: 'presets.names.logo_maker',
    logo: {
      url: 'https://www.wix.com/logo/maker/',
    },
    landingPage: {
      title: {
        key: 'emailStep.logo_maker.title',
      },
    },
    login: {
      title: {
        key: 'login.logo_maker.title',
      },
    },
    signup: {
      title: {
        key: 'signup.logo_maker.title',
      },
    },
  },
  [PRESETS.BUSINESS_CARD_MAKER]: {
    nameKey: 'presets.names.business_card_maker',
    logo: {
      url: 'https://www.wix.com/tools/business-card-maker/',
    },
    login: {
      title: {
        key: 'login.business_card_maker.title',
      },
    },
    signup: {
      title: {
        key: 'signup.business_card_maker.title',
      },
    },
  },

  [PRESETS.WIXEL]: {
    id: PRESETS.WIXEL,
    shouldDisablePostLoginSuggestions: true,
    nameKey: 'presets.names.wixel',
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 52px 0',
        },
        borderRadius: '24px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
      mobile: {
        primary: {
          padding: '36px 0 54px 0',
        },
        secondary: {
          padding: '36px 0 54px 0',
        },
        borderRadius: '24px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wixel-logo.svg',
      url: 'https://www.wix.com/wixel',
      height: '28px',
      alt: 'Wixel',
      mobile: {
        height: '24px',
      },
    },
    landingPage: {
      title: {
        // `emailStep.wixel.title` was never added to Babel; reuse the existing,
        // fully-translated branded signup title ("Get started with Wixel") so the
        // landing screen shows branded copy instead of a raw key.
        key: 'signup.wixel.title',
      },
      dividerTextKey: 'emailStep.wixel.or.login.with', // "or" divider between social and email
    },
    login: {
      title: {
        key: 'login.wixel.title',
      },
      subtitle: {
        key: 'login.wixel.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.wixel.primary_button',
        skin: 'standard',
        fullWidth: true,
      },
      alreadyHaveAccount: {
        title: {
          key: 'login.wixel.already_have_account',
        },
      },
      dividerTextKey: 'emailStep.wixel.or.login.with', // "or" divider between social and email
    },
    signup: {
      title: {
        key: 'signup.wixel.title',
      },
      subtitle: {
        key: 'signup.wixel.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.wixel.primary_button',
        skin: 'standard',
        fullWidth: true,
      },
      flows: [
        {
          id: 'image',
          title: {
            key: 'signup.wixel.image_flow.title',
          },
          subtitle: {
            key: 'signup.wixel.image_flow.subtitle',
          },
        },
      ],
      dividerTextKey: 'emailStep.wixel.signup.divider', // "or" divider between social and email
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
      showArrowOnButton: true,
    },
    titleCustomFontSize: {
      authPage: {
        desktop: 'xLarge',
        mobile: 'xLarge',
      },
      secondaryFlow: {
        desktop: 'xLarge',
        mobile: 'xLarge',
      },
    },
    cssVariables: {
      '--wds-loader-fill-loading': '#D6FFE1',
      '--wds-loader-fill': '#1AAC40',
      '--wds-button-fill-standard-primary': '#442894',
      '--wds-button-fill-standard-primary-hover': '#442894',
      '--wds-button-fill-standard-primary-active': '#442894',
      '--wds-button-fill-standard-secondary-hover': '#442894',
      '--wds-button-fill-standard-secondary-active': '#442894',
      '--wds-color-text-primary': 'black',
      '--wds-font-weight-medium': '400',
      '--wds-font-weight-heading-1': '400',
      '--wds-font-weight-heading-2': '400',
      '--wds-font-weight-heading-3': '400',
      '--wds-color-border-standard-secondary': '#442894',
      '--wds-color-fill-standard-tertiary-hover': '#ffffff',
      '--wds-font-line-height-body-tiny': '18px',
      '--wds-font-family-display': 'Madefor Display',
    },
    background: `linear-gradient(144.34deg, rgba(205, 230, 255, 0.7) 31.48%, #8FF4AA 120.33%), linear-gradient(180deg, #ADD7FF 61.16%, #D3EAFF 100%)`,
    policies: {
      policyKey: 'policies_section.signup.wixel',
    },
    newGoogleSdkSocialButtonDesign: COMMON_NEW_GOOGLE_SDK_SOCIAL_BUTTON_DESIGN,
  },
  [PRESETS.PICASSO]: {
    id: PRESETS.PICASSO,
    shouldDisablePostLoginSuggestions: true,
    nameKey: 'presets.names.wixel',
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 52px 0',
        },
        borderRadius: '24px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
      mobile: {
        primary: {
          padding: '36px 0 54px 0',
        },
        secondary: {
          padding: '36px 0 54px 0',
        },
        borderRadius: '24px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/picasso-logo.png',
      url: 'https://www.wix.com/picasso',
      height: '90px',
      alt: 'Picasso',
      mobile: {
        height: '24px',
      },
    },
    landingPage: {
      title: {
        // `emailStep.picasso.title` was never added to Babel; reuse the existing,
        // fully-translated branded signup title ("Get started with Picasso") so
        // the landing screen shows branded copy instead of a raw key.
        key: 'signup.picasso.title',
      },
      dividerTextKey: 'emailStep.picasso.or.login.with', // "or" divider between social and email
    },
    login: {
      title: {
        key: 'login.picasso.title',
      },
      subtitle: {
        key: 'login.picasso.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.picasso.primary_button',
        skin: 'dark',
        fullWidth: true,
      },
      alreadyHaveAccount: {
        title: {
          key: 'login.picasso.already_have_account',
        },
      },
      dividerTextKey: 'emailStep.picasso.or.login.with', // "or" divider between social and email
    },
    signup: {
      title: {
        key: 'signup.picasso.title',
      },
      subtitle: {
        key: 'signup.picasso.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.picasso.primary_button',
        skin: 'dark',
        fullWidth: true,
      },
      flows: [
        {
          id: 'image',
          title: {
            key: 'signup.picasso.image_flow.title',
          },
          subtitle: {
            key: 'signup.picasso.image_flow.subtitle',
          },
        },
      ],
      dividerTextKey: 'emailStep.picasso.signup.divider', // "or" divider between social and email
    },
    buttonDesign: {
      skin: 'dark',
      fullWidth: true,
      showArrowOnButton: true,
    },
    cssVariables: {
      '--wds-loader-fill-loading': '#D6FFE1',
      '--wds-loader-fill': '#1AAC40',
    },
    background: `url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/picasso-background.jpeg")`,
    // No `policies.policyKey`: `policies_section.signup.picasso` was never added
    // to Babel, so it rendered as a raw key. Falls back to the generic, translated
    // `policies_section.signup_content` (see PoliciesStore.expandedPolicyKey).
  },
  [PRESETS.WIX_2025]: {
    id: PRESETS.WIX_2025,
    shouldDisablePostLoginSuggestions: false,
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 48px 0',
        },
        borderRadius: '12px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
      mobile: {
        fullScreen: true, // Full-screen dialog on mobile (no card styling)
        primary: {
          padding: '12px 48px 36px 48px', // 48px horizontal = 24px card margin + 24px content padding from Figma
        },
        secondary: {
          padding: '12px 48px 36px 48px',
        },
        borderRadius: '0',
        boxShadow: 'none',
      },
    },
    logo: {
      height: '29px',
      mobile: {
        height: '18px',
      },
      dividerColor: '#F1F1F1', // Divider between Wix logo and account logo on SSO screens
    },
    socialButtonDesign: {
      borderColor: '#000000', // Black border per Figma (node 3808-12633)
      borderWidth: '1px', // Per Figma spec
      borderRadius: '6px', // Per Figma spec
      height: '43px',
      iconTextLayout: 'centered',
      fontSize: '14px',
    },
    newGoogleSdkSocialButtonDesign: {
      ...COMMON_NEW_GOOGLE_SDK_SOCIAL_BUTTON_DESIGN,
      height: '40px',
      borderRadius: '4px',
      iconTextLayout: 'centered',
      fontSize: '14px',
    },
    formSpacing: {
      inputButtonGap: '12px', // 12px gap between input and button when no forgot email link (Figma node 3808-12377)
      inputForgotEmailGap: '8px', // 8px gap between input and forgot email link when present
      forgotEmailButtonGap: '24px', // 24px gap between forgot email link and button
      policiesDividerSpacing: {
        top: '44px', // 44px above the horizontal divider (Figma node 3808-12646)
        centerVertically: true, // Center policies text between divider and bottom of card
      },
      titleSectionGap: '16px', // 16px gap between title and subtitle (Figma node 3808-12463)
      enterCodeButtonLayout: 'stacked', // Stacked full-width buttons in 2FA code dialogs (Figma node 3808-12663)
      secondaryDialogInputButtonGap: {
        desktop: '45px', // Total visual gap between input and button
      },
    },
    landingPage: {
      primaryButtonDesign: {
        skin: 'standard',
        fullWidth: true,
        hideSuffixIcon: true, // Hide ChevronRight icon to match Figma
      },
      dividerTextKey: 'emailStep.divider', // "or" divider between social and email
    },
    login: {
      primaryButtonDesign: {
        textKey: 'emailStep.submit', // "Continue with Email"
        skin: 'standard',
        fullWidth: true,
      },
      dividerTextKey: 'emailStep.divider', // "or" divider between social and email
    },
    signup: {
      showEmailCodeSignup: true,
      primaryButtonDesign: {
        textKey: 'emailStep.submit', // "Continue with Email" for first step
        skin: 'standard',
        fullWidth: true,
      },
      emailFormButtonTextKey: 'signup.submit', // "Sign Up" for second step (password form)
      dividerTextKey: 'signup.divider', // "or" divider between social and email
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
      height: '43px', // Figma spec from node 3808-12631
      fontSize: '14px', // Per Figma spec
      showArrowOnButton: false,
    },
    titleCustomFontSize: {
      authPage: {
        desktop: 'xLarge', // 36px with 16px margin-bottom (Figma node 3808-12631)
        mobile: 'xLarge', // 28px with 12px margin-bottom
      },
      secondaryFlow: {
        desktop: 'xLarge',
        mobile: 'xLarge',
      },
    },
    loaderColors: {
      fullPage: {
        fill: '#D5DFFF',
        fillLoading: '#2F5DFF',
      },
    },
    showCardLoader: true,
    cssVariables: {
      // Loader colors
      '--wds-loader-fill-loading': '#0F2CCF', // Primary spinner arc color
      '--wds-loader-fill': '#ffffff', // Background spinner arc color

      // Primary Button colors (Figma nodes: 3808-15760, 3808-16879, 3808-16886, 3808-16893)
      '--wds-button-fill-standard-primary': '#2f5dff', // Regular state (node 3808-15760)
      '--wds-button-fill-standard-primary-hover': '#1c3fe5', // Hover (node 3808-16879)
      '--wds-button-fill-standard-primary-active': '#0f2ccf', // Pressed (node 3808-16886)

      // Button border radius - override WDS default pill shape to rounded rectangle
      // Figma shows moderate border radius (node 3808-12628)
      '--wds-button-border-radius-medium': '6px',

      // Font family - Madefor Display for headings (Figma node 3808-12631)
      '--wds-font-family-display': 'Madefor Display',

      // Font weight - Medium weight for headings per 2025 spec
      '--wds-font-weight-heading-1': '500',

      // NOTE: These CSS variables follow the wixel preset pattern.
      // Additional button states (disabled, secondary) and input field colors
      // cannot be configured via CSS variables and require component-level SCSS overrides.
      // See docs/wix-2025-preset-notes.md for details on non-configurable styles.
    },
    // Switch link styling (Sign Up, Log In dialog switch links, Continue with SSO)
    switchLinkDesign: {
      color: '#2F5DFF',
      hoverColor: '#084EBD',
      focusRingColor: '#116DFF',
      underline: 'none',
      fontWeight: '500', // Medium
    },
    // Divider styling (between social buttons and email input)
    dividerDesign: {
      textColor: '#000000',
      lineColor: '#D4D4D4',
    },
    // Policies footer link styling
    policies: {
      linkColor: '#000624',
    },
    // Keep Brand theme enabled for input border colors
    useBrandTheme: true,
    // Override Brand theme's hardcoded secondary text color for policies/reCAPTCHA
    secondaryTextColor: '#42454C',
    // Background: responsive image-set with format fallbacks (AVIF → WebP → JPEG)
    // Browser automatically selects optimal format and resolution based on support and device pixel ratio
    background: `#f4f1f2 image-set(
      url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-new/wix-2025-new%401x.avif") type("image/avif") 1x,
      url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-new/wix-2025-new%402x.avif") type("image/avif") 2x,
      url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-new/wix-2025-new%401x.webp") type("image/webp") 1x,
      url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-new/wix-2025-new%402x.webp") type("image/webp") 2x,
      url("https://img-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-new/wix-2025-new.png/v1/fit/w_2048,h_1410,q_90/wix-2025-new.jpeg") 1x,
      url("https://img-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-new/wix-2025-new.png/v1/fit/w_4096,h_2820,q_90/wix-2025-new.jpeg") 2x
    ) center center/2048px 1410px no-repeat`,
    backButtonPosition: 'aboveDialog',
    mobileBackButtonConfig: {
      position: 'header',
      marginTop: '23px',
      marginStart: '20px',
      iconTextGap: '4px',
    },
    // Dialog vertically centered with minimum top offset
    dialogVerticalPosition: 'centered',
    dialogMinTopOffset: '100px',
  },
  // Symphony — Wix's proactive "team of agents" product for SMBs.
  // Design source: the "🟢 Symphony brand - MKT" Figma, node 1:11185
  // (New sign up / Login page). The MKT design is a NEUTRAL take on the
  // Wix-2025 base — a solid BLACK primary CTA, blue Wix switch links, Archivo
  // display headings and Symphony-branded copy. It intentionally drops the
  // earlier green ("Signals") theming, so most brand fields simply inherit the
  // Wix-2025 base and only the deltas below are overridden.
  [PRESETS.SYMPHONY]: {
    id: PRESETS.SYMPHONY,
    // Branded name: `presets.names.symphony` = "Symphony". Drives the {brand}
    // placeholder on shared screens (e.g. 2FA) and the branded titles below.
    nameKey: 'presets.names.symphony',
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 48px 0',
        },
        // Card radius per Figma (16px vs the 12px Wix-2025 base) with a soft,
        // low-opacity drop shadow (QA: border-radius 16px; box-shadow
        // 0 10px 30px rgba(0,0,0,0.05)).
        borderRadius: '16px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.05)',
      },
      mobile: {
        fullScreen: true, // Full-screen dialog on mobile (matches Wix-2025)
        // Top padding widened to 46px per Figma so the title sits 57px down from the
        // header logo on mobile (QA correction: the earlier ~45px target measured
        // between the wrong elements). The header sits flush above the full-screen
        // card, so this top inset IS the logo→title gap; ~11px comes from the
        // logo/heading leading, +46px padding ≈ 57px. Applied to both primary
        // (signup/login/email step) and secondary (2FA/SSO/reset) screens so the
        // logo→title spacing is consistent across all Symphony mobile screens.
        primary: {
          padding: '46px 48px 36px 48px',
        },
        secondary: {
          padding: '46px 48px 36px 48px',
        },
        borderRadius: '0',
        boxShadow: 'none',
      },
    },
    logo: {
      // "Symphony by wix" lockup, monochrome charcoal — exported from the MKT
      // Figma (node 4517:12183) to match the neutral design; bundled locally,
      // same pattern as the other locally-served brand logos.
      src: symphonyLogo,
      height: '29px',
      mobile: {
        height: '22px',
      },
      alt: 'Symphony',
      url: 'https://www.wix.com/symphony',
    },
    // Symphony-branded titles + subtitles per the MKT Figma. Keys added to
    // Babel (mirrors the wixel precedent). The button/divider config is repeated
    // here because the preset merge replaces these objects wholesale (shallow),
    // so it must restate the Wix-2025 base values it wants to keep.
    landingPage: {
      title: {
        key: 'emailStep.symphony.title', // "Get started with Symphony"
      },
      primaryButtonDesign: {
        skin: 'standard',
        fullWidth: true,
        hideSuffixIcon: true, // Hide ChevronRight icon to match Figma
      },
      dividerTextKey: 'emailStep.divider', // "or" divider between social and email
    },
    login: {
      title: {
        key: 'login.symphony.title', // "Log in to Symphony"
      },
      subtitle: {
        key: 'login.symphony.subtitle', // "Enter your account info."
      },
      primaryButtonDesign: {
        textKey: 'emailStep.submit', // "Continue with Email"
        skin: 'standard',
        fullWidth: true,
      },
      dividerTextKey: 'emailStep.divider',
    },
    signup: {
      title: {
        key: 'signup.symphony.title', // "Get started with Symphony"
      },
      subtitle: {
        key: 'signup.symphony.subtitle', // "Create a Wix account to continue."
      },
      primaryButtonDesign: {
        textKey: 'emailStep.submit', // "Continue with Email" for first step
        skin: 'standard',
        fullWidth: true,
      },
      emailFormButtonTextKey: 'signup.submit', // "Sign Up" for the password step
      dividerTextKey: 'signup.divider',
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
      height: '43px', // Match Wix-2025 base
      fontSize: '14px',
      showArrowOnButton: false,
      // Primary Button label + disabled text + loading spinner per Figma.
      // Applied inline-scoped to the button in ButtonWithLoader (the underlying
      // WDS tokens are shared) so the full-page loader and unrelated text are
      // untouched.
      textColor: '#F3F3F3',
      disabledTextColor: '#B5B5B5',
      loaderColors: {
        // Loader "On Dark BG" spinner per Figma: track #5E5E5E, arc #FFFFFF.
        // (The Figma's "#0F2CCF" label is the annotation leader-dot color, not
        // the rendered track — pixel-sampling the actual spinner gives grey
        // ~#606060 ≈ #5E5E5E, matching the Button sheet.)
        fill: '#5E5E5E', // spinner track on the black button
        fillLoading: '#FFFFFF', // spinner arc
      },
    },
    // Restates the Wix-2025 form spacing (shallow merge replaces the whole
    // object) but tightens the title→subtitle gap to 6px per the Figma
    // (node itemSpacing) — the base uses 16px.
    formSpacing: {
      inputButtonGap: '12px',
      inputForgotEmailGap: '8px',
      forgotEmailButtonGap: '24px',
      policiesDividerSpacing: {
        top: '44px',
        centerVertically: true,
      },
      titleSectionGap: '6px', // Figma: tight gap between title and subtitle
      enterCodeButtonLayout: 'stacked',
      secondaryDialogInputButtonGap: {
        desktop: '45px',
      },
    },
    // Solid black primary CTA per Figma (overrides the Wix-2025 blue). Loader
    // and switch-link colors are intentionally NOT set so they inherit the
    // Wix-2025 blue via the merge below.
    cssVariables: {
      // Primary Button states per Figma (Interactive Components → Custom brand
      // Symphony). Fill: regular #000000, hover #333232, pressed/active #5E5E5E.
      '--wds-button-fill-standard-primary': '#000000',
      '--wds-button-fill-standard-primary-hover': '#333232',
      '--wds-button-fill-standard-primary-active': '#5E5E5E',
      // Border radius 8px. The WDS button root reads
      // --wds-button-border-radius-medium regardless of size="large" (theme
      // default is 6px); pin medium, plus large for safety.
      '--wds-button-border-radius-medium': '8px',
      '--wds-button-border-radius-large': '8px',
      // Disabled fill #EAE9E9 (button-only token — used solely by
      // ButtonCore--disabled, safe to set globally). The disabled TEXT (#B5B5B5)
      // is applied inline-scoped in ButtonWithLoader (see buttonDesign
      // .disabledTextColor) because its token is shared across components.
      '--wds-color-fill-standard-primary-disabled': '#EAE9E9',
      // Archivo display headings per Figma, with a Madefor Display fallback so
      // locales Archivo doesn't cover (Cyrillic/Hebrew/Arabic/CJK) still render
      // via per-glyph font fallback. Archivo is loaded in index.ejs/index.vm.
      '--wds-font-family-display': "'Archivo', 'Madefor Display'",
      // Archivo SemiBold (600) for the title, per Figma (wix-2025 base is 500).
      '--wds-font-weight-heading-1': '600',
      // Button tab-focus ring per Figma/QA: a #000000 ring with a white gap
      // between the button and the ring (so it stays visible on the black CTA).
      // WDS draws the focus ring as a box-shadow reading this token (default a
      // single `0 0 0 3px` blue); it's defined at :root, so overriding it here
      // (applied on documentElement) recolors the ring on all buttons. Two
      // layers: 2px white halo, then a 2px black ring.
      '--wds-shadow-focus-standard': '0 0 0 2px #ffffff, 0 0 0 4px #000000',
      // "or" divider width — align it to the form field / button column (320px,
      // = socialButtonsWidth) instead of stretching to the full white card.
      // Consumed by AuthPage.scss `.dividerContainer` (max-width + centered);
      // unset for other presets → default full-width.
      '--divider-max-width': '320px',
    },
    // Full-page card loader in the Symphony brand color — BLACK, matching the
    // black CTA and the Figma "Animated Components → Custom brand (Symphony)"
    // loader (#000000 vs the #3B6BE1 default WDS). Overrides the inherited
    // wix-2025 blue loader.
    loaderColors: {
      fullPage: {
        // Loader on light BG per Figma: track #DDDDDD, moving arc #000000.
        fill: '#DDDDDD', // track
        fillLoading: '#000000', // moving arc
      },
    },
    // Email form ABOVE the social buttons on the login / email-step screens per
    // UX QA p3 ("Email goes first on login screens!" — Figma QA p3 node 89:309).
    // Only the login-side vertical screens read this flag (via
    // displayStore.verticalSocialFirst in EmailStep + Login); the dedicated
    // signup screen is always social-first (hardcoded socialFirst default in
    // AuthPage.VerticalBody), matching the signup Figma. The FROM_SIGNUP "you
    // already have an account" step was already form-first and is unaffected.
    socialFirst: false,
    // Social Button states per Figma (Custom brand Symphony). Neutral outlined
    // button: white fill, grey border, black label — with grey hover/pressed
    // fills, a red error border, and a light-track/dark-arc spinner. Consumed
    // via display.resolvedSocialButtonDesign → SocialLoginButton (.newDesign).
    socialButtonDesign: {
      height: '43px', // H43
      borderColor: '#969696', // regular border 1px
      borderWidth: '1px',
      borderRadius: '8px',
      // Icon sits directly next to the label (both centered as a group), per
      // Figma. Must be restated here: Symphony's socialButtonDesign REPLACES the
      // Wix-2025 base wholesale in the shallow `{...WIX_2025, ...SYMPHONY}` merge
      // below, so without this it falls back to the 'space-between' default
      // (icon pinned far-left).
      iconTextLayout: 'centered',
      // NOTE: intentionally no `backgroundColor` — the .newDesign base is
      // already #fff, and setting it inline would override the :hover/:active
      // background rules (inline styles beat pseudo-class rules).
      textColor: '#000000',
      hoverBackgroundColor: '#EAE9E9',
      hoverBorderColor: '#B5B5B5',
      activeBackgroundColor: '#DDDDDD',
      activeBorderColor: '#B5B5B5',
      disabledBackgroundColor: '#EAE9E9',
      disabledTextColor: '#B5B5B5',
      errorBorderColor: '#DF3336',
      loaderFill: '#F3F3F3', // spinner track
      loaderFillLoading: '#000000', // spinner arc
      focusOutline: '1.5px solid #000000', // black tab-focus ring per Figma
      focusOutlineOffset: '1px',
    },
    // "or" divider between social buttons and email input — line per Figma.
    dividerDesign: {
      textColor: '#000000',
      lineColor: '#EAE9E9',
    },
    // Switch links (Sign Up / Log In). Keep the inherited Wix-2025 BLUE link
    // color/hover, but use a BLACK tab-focus ring per Figma (the Wix-2025 base
    // uses a #116DFF blue ring). Restated in full because the shallow preset
    // merge replaces `switchLinkDesign` wholesale.
    switchLinkDesign: {
      color: '#2F5DFF',
      hoverColor: '#084EBD',
      focusRingColor: '#000000',
      underline: 'none',
      fontWeight: '500',
    },
    // Symphony-specific legal copy per Figma: "By signing up to Symphony, you'll
    // create a Wix account and agree to our Terms of Use…" (policyKey drives the
    // expanded policies sentence — see PoliciesStore.expandedPolicyKey). linkColor
    // restated because the shallow preset merge replaces the whole `policies`.
    policies: {
      policyKey: 'policies_section.symphony.signup_content',
      linkColor: '#000624',
      minimalFitContent: true,
    },
    // Subtitle typography per Figma (Madefor Text 22px / 21px line-height) —
    // the Wix-2025 base renders the preset subtitle at the smaller WDS "small"
    // size. Applied to the subtitle element in AuthPage, gated to this preset.
    subtitleFontSize: {
      // Subheader per Figma (Wix Madefor Text, Regular): 22px desktop / 18px
      // mobile, line-height 21, letter-spacing -1%.
      fontSize: '22px',
      lineHeight: '21px',
      mobileFontSize: '18px',
      letterSpacing: '-0.01em',
    },
    smallContextualSubtitle: true,
    // Symphony marketing background — official left/right avatar+stat element
    // strips pinned to each edge with a side-fade overlay. Applied via a CSS
    // class (see App.scss `.symphonyBackground`) rather than an inline
    // `background` so the bundled WebP/PNG assets are referenced from SCSS
    // (webpack-resolved) and never imported into JS — keeping them out of the
    // jest module graph. The strips stay on the sides at every desktop width.
    backgroundClassName: 'symphonyBackground',
    // Back button in the header, below the logo (left-aligned) per Figma —
    // rather than the Wix-2025 'aboveDialog' placement.
    backButtonPosition: 'header',
    // Back button per UX QA / design: arrow-left (←) + semibold label, 16px
    // desktop / 14px mobile (chevron was rejected in QA). Consumed in MainHeader.
    backButtonDesign: {
      chevronIcon: false,
      fontWeight: '600',
      fontSize: '16px',
      mobileFontSize: '14px',
      // UX QA p3 ("make icon smaller, height -3px" on the mobile app): shrink the
      // back arrow by 3px on mobile only. The WDS TextButton size="medium" affix
      // renders at ~18px, so 15px = −3px. Desktop keeps the WDS default.
      mobileIconSize: '15px',
    },
  },

  [PRESETS.FADE]: {
    id: PRESETS.FADE,
    // Fade (tryfade.ai, the AI video ad maker). Fade's sign-in reaches this
    // preset through `manage.wix.com/fade` (oauth2-ng CustomLoginConfig) and
    // `users.wix.com/fade/signin` (the `fade` userType). It replaces the retired
    // `vibu` preset, which carried the same design under the old name.
    // Tokens come from imaxx's brand kit (design/brand/tokens.json ->
    // resolved.json, light theme): the Afterimage violet palette, Inter Variable
    // display type, solid action fills for buttons and the accessible text accent
    // for links. Same vertical wix-2025 layout as Symphony/whenever; copy uses
    // only generic keys that exist in Babel.
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 48px 0',
        },
        borderRadius: '12px',
        boxShadow: '0px 10px 30px 0px rgba(87, 48, 206, 0.12)',
      },
      mobile: {
        primary: {
          padding: '36px 0 54px 0',
        },
        secondary: {
          padding: '36px 0 54px 0',
        },
        borderRadius: '12px',
        boxShadow: '0px 10px 30px 0px rgba(87, 48, 206, 0.12)',
      },
    },
    logo: {
      // The lockup is 1.5em tall (symbol box), so 28px ≈ 18.7px wordmark type.
      src: fadeLogo,
      height: '28px',
      mobile: {
        height: '24px',
      },
      alt: 'Fade',
      url: 'https://www.tryfade.ai',
    },
    landingPage: {
      // Filled, full-width primary CTA
      primaryButtonDesign: {
        skin: 'standard',
        fullWidth: true,
        hideSuffixIcon: true,
      },
      dividerTextKey: 'emailStep.divider', // generic "or" key (exists in Babel)
    },
    login: {
      primaryButtonDesign: {
        textKey: 'emailStep.submit',
        skin: 'standard',
        fullWidth: true,
      },
      dividerTextKey: 'emailStep.divider',
    },
    signup: {
      title: {
        key: 'signup.title', // generic key, exists in Babel
      },
      primaryButtonDesign: {
        textKey: 'emailStep.submit',
        skin: 'standard',
        fullWidth: true,
      },
      emailFormButtonTextKey: 'signup.submit',
      dividerTextKey: 'signup.divider',
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
    },
    // Roles, light theme: actionSolid / actionHover / actionPressed for the CTA
    // (violet600/700/800), violet100 loader track. Inter Variable is loaded in
    // index.ejs/index.vm for the fade userType (Wix statics unpkg mirror, like
    // Symphony's Archivo); Madefor Display is the fallback everywhere else.
    cssVariables: {
      '--wds-button-fill-standard-primary': '#742fe6',
      '--wds-button-fill-standard-primary-hover': '#6427e7',
      '--wds-button-fill-standard-primary-active': '#5730ce',
      '--wds-loader-fill-loading': '#742fe6',
      '--wds-loader-fill': '#efd4ff',
      '--wds-font-family-display': "'Inter Variable', 'Madefor Display'",
    },
    // Text actions (Sign Up / Log In / Continue with SSO) use the accessible text
    // accent (light `action` = violet700), not the button fill — the brand kit
    // keeps those two roles apart.
    switchLinkDesign: {
      color: '#6427e7',
      hoverColor: '#5730ce',
      focusRingColor: '#742fe6',
      underline: 'none',
      fontWeight: '500',
    },
    // Iris Tint neutrals: muted ink for the "or" text, violet-grey hairline.
    dividerDesign: {
      textColor: '#5e5b6b',
      lineColor: 'rgba(28, 27, 42, 0.14)',
    },
    policies: {
      linkColor: '#6427e7',
    },
    loaderColors: {
      fullPage: {
        fill: '#efd4ff',
        fillLoading: '#742fe6',
      },
    },
    secondaryTextColor: '#5e5b6b',
    // Iris Tint ground: white page with a faint violet wash at the top, settling
    // into the violet-grey element tone (--panel-2) at the bottom.
    background: `radial-gradient(ellipse 90% 70% at 50% 0%, rgba(116, 47, 230, 0.1) 0%, transparent 55%), linear-gradient(180deg, #ffffff 0%, #eaecf4 100%)`,
  },

  [PRESETS.WHENEVER]: {
    id: PRESETS.WHENEVER,
    // Branded login for whenever (whenever.dev). Tokens mirror
    // packages/whenever-brand/src/theme/wheneverTheme.ts (light mode).
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 48px 0',
        },
        borderRadius: '7px',
        boxShadow:
          '0 2px 2px rgba(0, 0, 0, 0.1), 0 6px 16px rgba(0, 0, 0, 0.12)',
      },
      mobile: {
        primary: {
          padding: '36px 0 54px 0',
        },
        secondary: {
          padding: '36px 0 54px 0',
        },
        borderRadius: '7px',
        boxShadow:
          '0 2px 2px rgba(0, 0, 0, 0.1), 0 6px 16px rgba(0, 0, 0, 0.12)',
      },
    },
    logo: {
      src: wheneverLogo,
      height: '28px',
      mobile: {
        height: '24px',
      },
      alt: 'Whenever',
      url: 'https://www.whenever.dev',
    },
    landingPage: {
      primaryButtonDesign: {
        skin: 'standard',
        fullWidth: true,
        hideSuffixIcon: true,
      },
      dividerTextKey: 'emailStep.divider',
    },
    login: {
      primaryButtonDesign: {
        textKey: 'emailStep.submit',
        skin: 'standard',
        fullWidth: true,
      },
      dividerTextKey: 'emailStep.divider',
    },
    signup: {
      title: {
        key: 'signup.title',
      },
      primaryButtonDesign: {
        textKey: 'emailStep.submit',
        skin: 'standard',
        fullWidth: true,
      },
      emailFormButtonTextKey: 'signup.submit',
      dividerTextKey: 'signup.divider',
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
      fontSize: '14px',
    },
    cssVariables: {
      '--wds-button-fill-standard-primary': '#7C3AED',
      '--wds-button-fill-standard-primary-hover': '#6D28D9',
      '--wds-button-fill-standard-primary-active': '#5B21B6',
      '--wds-loader-fill-loading': '#7C3AED',
      '--wds-loader-fill': '#F3F4F6',
      '--wds-font-family-display': "'Anybody', 'Madefor Display'",
    },
    switchLinkDesign: {
      color: '#7C3AED',
      hoverColor: '#6D28D9',
      focusRingColor: '#7C3AED',
      underline: 'none',
      fontWeight: '500',
    },
    dividerDesign: {
      textColor: '#5A5851',
      lineColor: '#11111126',
    },
    policies: {
      linkColor: '#7C3AED',
    },
    loaderColors: {
      fullPage: {
        fill: '#F3F4F6',
        fillLoading: '#7C3AED',
      },
    },
    secondaryTextColor: '#5A5851',
    backgroundClassName: 'wheneverBackground',
  },

  // inherited from the Wix-2025 base until the Brik design lands.
  // Branded login for Brik, per Figma 105:10787.
  //
  // Inherits the Wix-2025 base via the spread at the bottom of this file, the
  // same way Symphony does — it keeps the structural spacing, dialog centering
  // and generic keys, and overrides only what Brik's design changes.
  //
  // ⚠️ A Brik rebrand is in flight: the logo, tile images and display font are
  // expected to change. The structural values here (radii, sizes, layout,
  // socialFirst) come from the UX and are expected to survive it.
  [PRESETS.BRIK]: {
    id: PRESETS.BRIK,
    // Drives the {brand} placeholder on shared screens (e.g. 2FA).
    nameKey: 'presets.names.brik',
    container: {
      layout: 'vertical',
      desktop: {
        // Figma: the card frame is paddingTop 48 / paddingBottom 56.
        primary: {
          padding: '48px 0 56px 0',
        },
        secondary: {
          padding: '48px 0 56px 0',
        },
        // Figma (105:10787): the card is a grid cell — 536x620 at x 691,
        // y 230, pure white on a #F7F7F7 page, square corners. The node
        // carries a DROP_SHADOW of rgba(0,0,0,0.04) at offset 1,1 blur 80:
        // wide and almost invisible, but it is what keeps the white cell from
        // reading as a flat cut-out against the page.
        borderRadius: '0',
        boxShadow: '1px 1px 80px 0px rgba(0, 0, 0, 0.04)',
      },
      mobile: {
        fullScreen: true,
        primary: {
          padding: '46px 48px 36px 48px',
        },
        secondary: {
          padding: '46px 48px 36px 48px',
        },
        borderRadius: '0',
        boxShadow: 'none',
      },
    },
    logo: {
      // BRIK wordmark, re-exported from Figma 105:10831 at 85x25. The BETA
      // pill is `visible: false` on that node, so the new export drops it —
      // the previous asset was 193x39 and carried the pill.
      src: brikLogo,
      height: '24px',
      mobile: {
        height: '18px',
      },
      alt: 'Brik',
      url: 'https://brik.space',
    },
    // Copy lives in Babel (project Login-React-SSO-App, 8232e6c2-…), taken
    // from Figma 105:10787. Both screens share the title; only the subtitle
    // differs — signup "Create a Brik account to continue.", login "Sign in
    // to continue". Keys are DRAFT/NOT_REVIEWED, so every non-en locale
    // serves the English source until localization translates them.
    landingPage: {
      title: {
        key: 'emailStep.brik.title',
      },
      primaryButtonDesign: {
        skin: 'standard',
        fullWidth: true,
        hideSuffixIcon: true,
      },
      dividerTextKey: 'emailStep.divider',
    },
    login: {
      title: {
        key: 'login.brik.title',
      },
      subtitle: {
        key: 'login.brik.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'emailStep.submit',
        skin: 'standard',
        fullWidth: true,
      },
      dividerTextKey: 'emailStep.divider',
    },
    signup: {
      title: {
        key: 'signup.brik.title',
      },
      subtitle: {
        key: 'signup.brik.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'emailStep.submit',
        skin: 'standard',
        fullWidth: true,
      },
      emailFormButtonTextKey: 'signup.submit',
      dividerTextKey: 'signup.divider',
    },
    // Figma: black CTA, 44px tall, label Early Sans Medium 12px on white.
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
      height: '44px',
      fontSize: '12px',
      showArrowOnButton: false,
      textColor: '#FFFFFF',
      disabledTextColor: '#B5B5B5',
      loaderColors: {
        fill: '#5E5E5E',
        fillLoading: '#FFFFFF',
      },
    },
    // Restates the Wix-2025 spacing (the shallow merge replaces the whole
    // object) with the Figma's 8px title→subtitle gap.
    formSpacing: {
      inputButtonGap: '12px',
      inputForgotEmailGap: '8px',
      forgotEmailButtonGap: '24px',
      policiesDividerSpacing: {
        top: '44px',
        centerVertically: true,
      },
      titleSectionGap: '8px',
      enterCodeButtonLayout: 'stacked',
      secondaryDialogInputButtonGap: {
        desktop: '45px',
      },
    },
    cssVariables: {
      // Black primary CTA per Figma (overrides the Wix-2025 blue).
      '--wds-button-fill-standard-primary': '#000000',
      '--wds-button-fill-standard-primary-hover': '#333232',
      '--wds-button-fill-standard-primary-active': '#5E5E5E',
      // Figma (105:10787): square CTA. Verified by measurement — the first
      // row of the button starts at the same x as its middle row, so there is
      // no corner rounding. The WDS button root reads the medium token
      // regardless of size="large", so pin both.
      '--wds-button-border-radius-medium': '0',
      '--wds-button-border-radius-large': '0',
      '--wds-color-fill-standard-primary-disabled': '#EAE9E9',
      // HAL Matex is Brik's brand face (Figma 105:10787). Neue Haas was the
      // *previous* one and is now only a fallback — Brik's own
      // `--brik-font-regular` stack in clay (`src/styles/tokens-v2.css`)
      // orders them exactly this way.
      //
      // The face is bundled and @font-face'd in App.scss. TODO(brik-fonts):
      // move it to the `presetDisplayFonts` map in index.ejs / index.vm, the
      // way Symphony loads Archivo, once the cut is published somewhere the
      // Wix statics unpkg mirror can serve.
      '--wds-font-family-display':
        "'HAL Matex', 'Neue Haas Grotesk Display Pro', 'Madefor Display'",
      // Figma title is 36px/500 — the Wix-2025 base weight already matches.
      '--wds-font-weight-heading-1': '500',
      // Keep the focus ring visible on the black CTA: white halo, black ring.
      '--wds-shadow-focus-standard': '0 0 0 2px #ffffff, 0 0 0 4px #000000',
      // Align the "or" divider to the 320px control column, not the full card.
      '--divider-max-width': '320px',
    },
    loaderColors: {
      fullPage: {
        fill: '#DDDDDD',
        fillLoading: '#000000',
      },
    },
    // Figma shows the social buttons above the email field on both screens,
    // which is the default — stated explicitly so a future base change can't
    // silently flip it.
    socialFirst: true,
    // Both fields point at the same object. Without the second one the
    // WIX_2025 spread leaves Brik inheriting Wix-2025's Google-SDK override,
    // and `DisplayStore.resolvedSocialButtonDesign` spreads that over this
    // design whenever `specs.ident.UseNewGoogleSdkReactLoginApp` is on — the
    // production path — rendering rounded 40px blue-hover buttons instead of
    // the square 46px ones. Same fix Explainers uses above.
    socialButtonDesign: BRIK_SOCIAL_BUTTON_DESIGN,
    newGoogleSdkSocialButtonDesign: BRIK_SOCIAL_BUTTON_DESIGN,
    dividerDesign: {
      textColor: '#333333',
      lineColor: '#E1E1E1',
    },
    switchLinkDesign: {
      color: '#000000',
      hoverColor: '#333232',
      focusRingColor: '#000000',
      underline: 'none',
      fontWeight: '500',
    },
    policies: {
      linkColor: '#000624',
      minimalFitContent: true,
    },
    secondaryTextColor: '#333333',
    // Three tiles on a #F7F7F7 base — see .brikBackground in App.scss. A class,
    // not an inline `background`, so the assets stay out of the jest graph.
    backgroundClassName: 'brikBackground',
  },
  [PRESETS.EXPLAINERS]: {
    id: PRESETS.EXPLAINERS,
    // Branded login for Explainers (explainers.sukurys.workers.dev), an
    // internal Wix POC that turns a topic into a narrated explainer video.
    // Query-route only for now (`?preset=explainers`) — no backend userType
    // yet. Tokens mirror the app's own stylesheet: #f3f3f3 canvas, white
    // 16px-radius cards, black pill CTAs, #969696 outlines, Archivo display
    // type and the #a4ff70 lime accent.
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 48px 0',
        },
        borderRadius: '16px',
        boxShadow:
          '0 8px 30px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)',
      },
      mobile: {
        fullScreen: true,
        primary: {
          padding: '12px 48px 36px 48px',
        },
        secondary: {
          padding: '12px 48px 36px 48px',
        },
        borderRadius: '0',
        boxShadow: 'none',
      },
    },
    logo: {
      src: explainersLogo,
      height: '28px',
      mobile: {
        height: '24px',
      },
      alt: 'Explainers',
      url: 'https://explainers.sukurys.workers.dev',
    },
    // Generic, already-translated copy keys only (the fade/whenever approach),
    // so no Babel keys are needed.
    landingPage: {
      primaryButtonDesign: {
        skin: 'standard',
        fullWidth: true,
        hideSuffixIcon: true,
      },
      dividerTextKey: 'emailStep.divider',
    },
    login: {
      primaryButtonDesign: {
        textKey: 'emailStep.submit',
        skin: 'standard',
        fullWidth: true,
      },
      dividerTextKey: 'emailStep.divider',
    },
    signup: {
      title: {
        key: 'signup.title',
      },
      primaryButtonDesign: {
        textKey: 'emailStep.submit',
        skin: 'standard',
        fullWidth: true,
      },
      emailFormButtonTextKey: 'signup.submit',
      dividerTextKey: 'signup.divider',
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
      height: '43px',
      fontSize: '14px',
      showArrowOnButton: false,
      textColor: '#F3F3F3',
      disabledTextColor: '#B5B5B5',
      loaderColors: {
        fill: '#5E5E5E',
        fillLoading: '#A4FF70',
      },
    },
    cssVariables: {
      '--wds-button-fill-standard-primary': '#000000',
      '--wds-button-fill-standard-primary-hover': '#262626',
      '--wds-button-fill-standard-primary-active': '#3D3D3D',
      '--wds-color-fill-standard-primary-disabled': '#EAE9E9',
      // Pill buttons, as everywhere in the app. The WDS button root reads the
      // medium radius regardless of size; pin large too (Symphony precedent).
      '--wds-button-border-radius-medium': '9999px',
      '--wds-button-border-radius-large': '9999px',
      '--wds-loader-fill-loading': '#000000',
      '--wds-loader-fill': '#A4FF70',
      // Archivo is only loaded for the symphony userType, so on the query
      // route headings fall back to Madefor Display.
      '--wds-font-family-display': "'Archivo', 'Madefor Display'",
      '--wds-font-weight-heading-1': '600',
      '--wds-shadow-focus-standard': '0 0 0 2px #ffffff, 0 0 0 4px #000000',
      '--divider-max-width': '320px',
    },
    socialButtonDesign: EXPLAINERS_SOCIAL_BUTTON_DESIGN,
    newGoogleSdkSocialButtonDesign: EXPLAINERS_SOCIAL_BUTTON_DESIGN,
    switchLinkDesign: {
      color: '#000000',
      hoverColor: '#5E5E5E',
      focusRingColor: '#000000',
      underline: 'always',
      fontWeight: '500',
    },
    dividerDesign: {
      textColor: 'rgba(0, 0, 0, 0.6)',
      lineColor: '#EAE9E9',
    },
    policies: {
      linkColor: '#000000',
    },
    loaderColors: {
      fullPage: {
        fill: '#A4FF70',
        fillLoading: '#000000',
      },
    },
    secondaryTextColor: 'rgba(0, 0, 0, 0.6)',
    background: '#f3f3f3',
  },
};

// Symphony inherits the Wix-2025 preset as its base, then layers its own brand
// identity on top. From Wix-2025 it picks up everything it doesn't define itself:
// social-button styling, form spacing, full-page card loader + blue loader
// colors, blue switch links, blue policy links, title font sizes, secondary text
// color, Brand-theme flag, dialog centering and back-button placement.
// Symphony's own fields (nameKey, logo, container radius, branded title/subtitle
// keys, black CTA, Archivo font, Figma divider line, marketing background) win.
// cssVariables are deep-merged so the black CTA + Archivo font override while
// Wix-2025's structural vars (button border radius, heading font-weight, blue
// loader) are still inherited.
ViewPresets[PRESETS.SYMPHONY] = {
  ...ViewPresets[PRESETS.WIX_2025],
  ...ViewPresets[PRESETS.SYMPHONY],
  cssVariables: {
    ...ViewPresets[PRESETS.WIX_2025].cssVariables,
    ...ViewPresets[PRESETS.SYMPHONY].cssVariables,
  },
};

// Fade inherits the Wix-2025 base the same way Symphony does:
// it picks up everything it doesn't define (social-button styling, form
// spacing, title font sizes, dialog centering, generic translated title/subtitle
// keys, etc.), while its own fields (logo, container, violet CTA/links/divider/
// policies, loaderColors, secondary text color, background) win. cssVariables
// are shallow-merged one level deep so the brand colors and Inter display font
// override but Wix-2025's structural vars (button radius, heading weight) are
// kept.
ViewPresets[PRESETS.FADE] = {
  ...ViewPresets[PRESETS.WIX_2025],
  ...ViewPresets[PRESETS.FADE],
  cssVariables: {
    ...ViewPresets[PRESETS.WIX_2025].cssVariables,
    ...ViewPresets[PRESETS.FADE].cssVariables,
  },
};

ViewPresets[PRESETS.WHENEVER] = {
  ...ViewPresets[PRESETS.WIX_2025],
  ...ViewPresets[PRESETS.WHENEVER],
  cssVariables: {
    ...ViewPresets[PRESETS.WIX_2025].cssVariables,
    ...ViewPresets[PRESETS.WHENEVER].cssVariables,
  },
};

// Explainers inherits the Wix-2025 base the same way whenever does; its own
// fields (logo, container, black pill CTA, outlined pill social buttons, black
// links, lime loaders, flat #f3f3f3 background) win.
ViewPresets[PRESETS.EXPLAINERS] = {
  ...ViewPresets[PRESETS.WIX_2025],
  ...ViewPresets[PRESETS.EXPLAINERS],
  cssVariables: {
    ...ViewPresets[PRESETS.WIX_2025].cssVariables,
    ...ViewPresets[PRESETS.EXPLAINERS].cssVariables,
  },
};

ViewPresets[PRESETS.BRIK] = {
  ...ViewPresets[PRESETS.WIX_2025],
  ...ViewPresets[PRESETS.BRIK],
  cssVariables: {
    ...ViewPresets[PRESETS.WIX_2025].cssVariables,
    ...ViewPresets[PRESETS.BRIK].cssVariables,
  },
};
